import { useId } from "react";
import { EditableField, fieldDescription, useFieldSave } from "@/components/ds/EditableField";

/**
 * Les champs qui s'enregistrent seuls, à la sortie.
 *
 * Ils vivaient dans `CompanyPage`, en bas du fichier. L'écran de configuration de
 * l'école a besoin des mêmes, et les recopier aurait créé deux `TextField` qui
 * divergent au premier ajustement — le compteur ici, le plafond là.
 *
 * ⚠️ Chacun tire son `id` de `useId` et le passe à `EditableField`. Ils ne le
 * faisaient pas : le libellé était un `span` posé au-dessus d'un champ sans nom,
 * et un lecteur d'écran annonçait « zone de texte » sept fois de suite sur la page
 * de l'école. Le nom, l'aide et le compteur passent par le même identifiant.
 */

export function TextField({
	label,
	hint,
	max,
	value,
	onSave,
}: {
	label: string;
	hint?: string;
	max?: number;
	value: string;
	onSave: (value: string) => Promise<boolean>;
}) {
	const id = useId();
	const field = useFieldSave(value, onSave);
	return (
		<EditableField
			htmlFor={id}
			label={label}
			hint={hint}
			state={field.state}
			counter={max ? `${field.value.length} / ${max}` : undefined}
		>
			<input
				id={id}
				className="mg-field"
				maxLength={max}
				aria-describedby={fieldDescription(id, { message: Boolean(hint), counter: Boolean(max) })}
				value={field.value}
				onChange={(event) => field.setValue(event.target.value)}
				onBlur={() => field.commit()}
			/>
		</EditableField>
	);
}

export function TextAreaField({
	label,
	hint,
	max,
	rows = 6,
	value,
	onSave,
}: {
	label: string;
	hint?: string;
	max: number;
	rows?: number;
	value: string;
	onSave: (value: string) => Promise<boolean>;
}) {
	const id = useId();
	const field = useFieldSave(value, onSave);
	return (
		<EditableField
			htmlFor={id}
			label={label}
			hint={hint}
			state={field.state}
			counter={`${field.value.length} / ${max}`}
		>
			<textarea
				id={id}
				rows={rows}
				className="mg-field resize-y"
				maxLength={max}
				aria-describedby={fieldDescription(id, { message: Boolean(hint), counter: true })}
				value={field.value}
				onChange={(event) => field.setValue(event.target.value)}
				onBlur={() => field.commit()}
			/>
		</EditableField>
	);
}

export function SelectField({
	label,
	hint,
	options,
	emptyLabel = "Non renseigné",
	value,
	onSave,
}: {
	label: string;
	hint?: string;
	options: readonly string[];
	emptyLabel?: string;
	value: string;
	onSave: (value: string) => Promise<boolean>;
}) {
	const id = useId();
	const field = useFieldSave(value, onSave);
	return (
		<EditableField htmlFor={id} label={label} hint={hint} state={field.state}>
			<select
				id={id}
				className="mg-field"
				aria-describedby={fieldDescription(id, { message: Boolean(hint) })}
				value={field.value}
				// Un menu déroulant se valide AU CHOIX, pas à la sortie : choisir une
				// option ne fait pas perdre le focus au `select`, et attendre le `blur`
				// laissait « Enregistré » ne jamais s'afficher tant qu'on ne cliquait pas
				// ailleurs. La valeur est passée explicitement — `field.value` porte
				// encore l'ancienne à cet instant.
				onChange={(event) => {
					field.setValue(event.target.value);
					void field.commit(event.target.value);
				}}
			>
				<option value="">{emptyLabel}</option>
				{options.map((option) => (
					<option key={option} value={option}>
						{option}
					</option>
				))}
			</select>
		</EditableField>
	);
}

export function NumberField({
	label,
	value,
	min,
	max,
	onSave,
}: {
	label: string;
	value: number | null;
	min?: number;
	max?: number;
	onSave: (value: number | null) => Promise<boolean>;
}) {
	const id = useId();
	const field = useFieldSave(value === null ? "" : String(value), async (raw) =>
		onSave(raw === "" ? null : Number(raw)),
	);
	return (
		<EditableField htmlFor={id} label={label} state={field.state}>
			<input
				id={id}
				type="number"
				inputMode="numeric"
				min={min}
				max={max}
				className="mg-field"
				value={field.value}
				onChange={(event) => field.setValue(event.target.value)}
				onBlur={() => field.commit()}
			/>
		</EditableField>
	);
}

// ── Les champs d'un GESTE ────────────────────────────────────────────────────────
//
// Ceux du dessus s'enregistrent seuls, à la sortie. Ceux-ci ne s'enregistrent PAS :
// leur valeur part avec un bouton (« Publier », « Envoyer »), parce qu'une date
// limite ou un motif de refus n'a de sens qu'avec la décision qu'il accompagne.
// Même enveloppe (`EditableField`), sans état d'enregistrement, avec un vrai
// `label` relié au contrôle et une erreur qui prend la place de l'aide.
//
// Le bouton du geste reste ACTIF : un bouton grisé ne dit pas ce qui manque. C'est
// l'erreur posée sous le champ manquant, au clic, qui le dit (`error`).

export function TextInput({
	id: idImpose,
	label,
	hint,
	error,
	max,
	type = "text",
	required = false,
	placeholder,
	autoComplete,
	value,
	onChange,
}: {
	/** Un `id` fixe, quand un script ou un test vise le champ (`#email` des portes). */
	id?: string;
	label: string;
	hint?: string;
	error?: string | null;
	max?: number;
	type?: "text" | "email" | "url";
	required?: boolean;
	placeholder?: string;
	autoComplete?: string;
	value: string;
	onChange: (value: string) => void;
}) {
	const genere = useId();
	const id = idImpose ?? genere;
	return (
		<EditableField htmlFor={id} label={label} hint={hint} error={error} required={required}>
			<input
				id={id}
				type={type}
				className="mg-field"
				maxLength={max}
				required={required}
				aria-required={required || undefined}
				aria-invalid={error ? true : undefined}
				aria-describedby={fieldDescription(id, { message: Boolean(error || hint) })}
				placeholder={placeholder}
				autoComplete={autoComplete}
				value={value}
				onChange={(event) => onChange(event.target.value)}
			/>
		</EditableField>
	);
}

export function TextAreaInput({
	label,
	hint,
	error,
	required = false,
	max,
	rows = 3,
	value,
	onChange,
}: {
	label: string;
	hint?: string;
	error?: string | null;
	required?: boolean;
	max: number;
	rows?: number;
	value: string;
	onChange: (value: string) => void;
}) {
	const id = useId();
	return (
		<EditableField
			htmlFor={id}
			label={label}
			hint={hint}
			error={error}
			required={required}
			counter={`${value.length} / ${max}`}
		>
			<textarea
				id={id}
				rows={rows}
				className="mg-field resize-y"
				maxLength={max}
				aria-required={required || undefined}
				aria-invalid={error ? true : undefined}
				aria-describedby={fieldDescription(id, { message: Boolean(error || hint), counter: true })}
				value={value}
				onChange={(event) => onChange(event.target.value)}
			/>
		</EditableField>
	);
}

/** Un jour, au format `AAAA-MM-JJ`. `min` refuse un jour passé dès le sélecteur. */
export function DateInput({
	label,
	hint,
	error,
	required = false,
	min,
	value,
	onChange,
}: {
	label: string;
	hint?: string;
	error?: string | null;
	required?: boolean;
	min?: string;
	value: string;
	onChange: (value: string) => void;
}) {
	const id = useId();
	return (
		<EditableField htmlFor={id} label={label} hint={hint} error={error} required={required}>
			<input
				id={id}
				type="date"
				className="mg-field"
				min={min}
				required={required}
				aria-invalid={error ? true : undefined}
				aria-describedby={fieldDescription(id, { message: Boolean(error || hint) })}
				value={value}
				onChange={(event) => onChange(event.target.value)}
			/>
		</EditableField>
	);
}

export function SelectInput({
	label,
	hint,
	options,
	value,
	onChange,
}: {
	label: string;
	hint?: string;
	options: readonly { value: string; label: string }[];
	value: string;
	onChange: (value: string) => void;
}) {
	const id = useId();
	return (
		<EditableField htmlFor={id} label={label} hint={hint}>
			<select
				id={id}
				className="mg-field"
				aria-describedby={fieldDescription(id, { message: Boolean(hint) })}
				value={value}
				onChange={(event) => onChange(event.target.value)}
			>
				{options.map((option) => (
					<option key={option.value} value={option.value}>
						{option.label}
					</option>
				))}
			</select>
		</EditableField>
	);
}

/** Ce qu'un geste refusé dit à la personne. Rien quand il n'y a rien à dire. */
export function GestureError({ message }: { message: string | null }) {
	if (!message) return null;
	return (
		<p role="alert" className="t-body text-mg-error-fg">
			{message}
		</p>
	);
}
